/**
 * Your accuracy record.
 *
 * THE HONESTY CONTRACT, and it is not decoration:
 *
 * 1. A hit rate here measures how often a reading felt true TO YOU. You read
 *    the words before you judged them, so this is a record of your own
 *    judgement — never evidence that a system predicts events. Every surface
 *    below says so in the user's own language.
 * 2. Small samples never get a confident percentage. Three ratings cannot
 *    produce "67% accurate"; they produce "2 of 3, far too early to say".
 * 3. Where a number IS shown it carries its uncertainty visibly — a Wilson
 *    score interval drawn as a band behind the point estimate. Two traditions
 *    are only ever declared different when their intervals do not overlap.
 *
 * This is the one screen where being unimpressive is the correct behaviour.
 */

import { useMemo } from 'react';
import {
  ASTRO_SYSTEMS,
  HOROSCOPE_PERIODS,
  LIFE_AREAS,
  type AccuracyStats,
  type AccuracyTally,
  type AstroSystem,
  type HoroscopePeriod,
  type LifeArea,
} from '@astrally/shared';
import { Section } from '../../components/common';
import { creditOf, decisiveOf, overallTally } from './journalStore';

/** Below this many decisive ratings, no percentage is shown anywhere. */
const MIN_FOR_NUMBER = 5;
/** Below this, two traditions are never compared, however far apart they look. */
const MIN_FOR_COMPARE = 8;

export const SYSTEM_LABEL: Record<AstroSystem, string> = {
  vedic: 'Jyotish',
  western: 'Western',
};

export const AREA_LABEL: Record<LifeArea, string> = {
  career: 'Work',
  money: 'Money',
  relationships: 'Relationships',
  health: 'Health & energy',
  learning: 'Learning',
  home: 'Home & family',
  travel: 'Travel & moving',
  inner: 'Inner life',
};

const PERIOD_LABEL: Record<HoroscopePeriod, string> = {
  day: 'Daily',
  week: 'Weekly',
  month: 'Monthly',
  year: 'Yearly',
};

// ---------------------------------------------------------------------------
// Uncertainty
// ---------------------------------------------------------------------------

export interface Interval {
  lo: number;
  hi: number;
}

/**
 * Wilson score interval, 95%.
 *
 * Chosen over the textbook normal approximation because at n = 6 the normal
 * one happily reports bounds below 0 or above 1, which would be a lie drawn on
 * screen. Partials count half a hit, so `successes` is not an integer and the
 * interval is indicative rather than exact — it is used only to decide when a
 * number is worth showing and when two traditions may be called different,
 * both of which it is more than precise enough for.
 */
export function wilsonInterval(successes: number, n: number, z = 1.96): Interval {
  if (n <= 0) return { lo: 0, hi: 1 };
  const p = successes / n;
  const z2 = z * z;
  const denom = 1 + z2 / n;
  const centre = (p + z2 / (2 * n)) / denom;
  const margin = (z / denom) * Math.sqrt((p * (1 - p)) / n + z2 / (4 * n * n));
  return { lo: Math.max(0, centre - margin), hi: Math.min(1, centre + margin) };
}

const pct = (v: number) => `${Math.round(v * 100)}%`;

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

/**
 * One rate, drawn with its uncertainty. The pale band is the range the true
 * figure plausibly sits in; the bright tick is the single best guess. A wide
 * band is the point — it is how "I have barely rated anything" looks.
 */
function RateBar({ tally }: { tally: AccuracyTally }) {
  const n = decisiveOf(tally);
  const rate = n === 0 ? 0 : creditOf(tally) / n;
  const { lo, hi } = wilsonInterval(creditOf(tally), n);
  return (
    <div
      style={{
        position: 'relative',
        height: 10,
        borderRadius: 999,
        background: 'var(--card-strong)',
        border: '1px solid var(--border)',
        overflow: 'hidden',
      }}
      role="img"
      aria-label={
        n === 0
          ? 'Nothing rated yet'
          : `${pct(rate)}, plausibly between ${pct(lo)} and ${pct(hi)} on ${n} ratings`
      }
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: `${lo * 100}%`,
          width: `${Math.max(hi - lo, 0) * 100}%`,
          background: 'var(--mode)',
          opacity: 0.26,
        }}
      />
      {n > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `calc(${rate * 100}% - 1.5px)`,
            width: 3,
            background: 'var(--mode)',
            borderRadius: 2,
          }}
        />
      )}
    </div>
  );
}

/** Counts, always. These are facts; the percentage above them is an estimate. */
function TallyLine({ tally }: { tally: AccuracyTally }) {
  const unclear = tally.rated - decisiveOf(tally);
  return (
    <div className="tiny faint">
      {tally.hits} landed · {tally.partials} partly · {tally.misses} missed
      {unclear > 0 ? ` · ${unclear} couldn’t tell` : ''}
    </div>
  );
}

/**
 * A rate with its honesty gate applied. Under `MIN_FOR_NUMBER` decisive
 * ratings this renders counts and a nudge instead of a percentage — a
 * confident-looking number off three taps is the single most misleading thing
 * this screen could do.
 */
function RateBlock({
  label,
  tally,
  mode,
  hint,
}: {
  label: string;
  tally: AccuracyTally;
  /** Sets the tradition palette for the bar. Omit for the neutral accent. */
  mode?: AstroSystem;
  hint?: string;
}) {
  const n = decisiveOf(tally);
  const enough = n >= MIN_FOR_NUMBER;
  const rate = n === 0 ? 0 : creditOf(tally) / n;
  return (
    <div data-mode={mode} style={{ flex: 1, minWidth: 0 }}>
      <div className="spread" style={{ alignItems: 'baseline', marginBottom: 6 }}>
        <span className="h3" style={{ color: mode ? 'var(--mode-ink)' : 'var(--text)' }}>
          {label}
        </span>
        <span
          className={enough ? 'h3' : 'tiny faint'}
          style={enough ? { color: 'var(--mode-ink)' } : undefined}
        >
          {enough ? pct(rate) : `${tally.rated} rated`}
        </span>
      </div>
      <RateBar tally={tally} />
      <div style={{ marginTop: 6 }}>
        {enough ? (
          <TallyLine tally={tally} />
        ) : (
          <div className="tiny faint">
            {n === 0
              ? 'Nothing decisive rated yet.'
              : `Only ${n} rated — ${MIN_FOR_NUMBER - n} more before a percentage means anything.`}
          </div>
        )}
      </div>
      {hint && (
        <div className="tiny muted" style={{ marginTop: 4, lineHeight: 1.5 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

/** Compact row for the life-area and period breakdowns. */
function BreakdownRow({ label, tally, mode }: { label: string; tally: AccuracyTally; mode?: AstroSystem }) {
  const n = decisiveOf(tally);
  const enough = n >= MIN_FOR_NUMBER;
  const rate = n === 0 ? 0 : creditOf(tally) / n;
  return (
    <div data-mode={mode} className="row" style={{ gap: 10, padding: '7px 0' }}>
      <span className="small" style={{ width: '38%', flexShrink: 0 }}>
        {label}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <RateBar tally={tally} />
      </span>
      <span
        className="tiny"
        style={{
          width: 62,
          textAlign: 'right',
          flexShrink: 0,
          color: enough ? 'var(--mode-ink)' : 'var(--text-faint)',
          fontWeight: enough ? 700 : 400,
        }}
      >
        {enough ? pct(rate) : `${tally.rated}×`}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Which tradition reads you better
// ---------------------------------------------------------------------------

export interface SystemVerdict {
  /** Null whenever the data cannot support a claim — the common case. */
  leader: AstroSystem | null;
  message: string;
}

/**
 * Only calls a winner when BOTH traditions clear the sample floor AND their
 * confidence intervals do not overlap. Anything softer than that turns "I
 * rated nine things" into a belief about which system is true.
 */
export function compareSystems(stats: AccuracyStats): SystemVerdict {
  const [a, b] = ASTRO_SYSTEMS;
  const ta = stats.bySystem[a];
  const tb = stats.bySystem[b];
  const na = decisiveOf(ta);
  const nb = decisiveOf(tb);

  if (na < MIN_FOR_COMPARE || nb < MIN_FOR_COMPARE) {
    const short = na < nb ? a : b;
    const need = MIN_FOR_COMPARE - Math.min(na, nb);
    return {
      leader: null,
      message:
        na === 0 && nb === 0
          ? `Rate readings in both traditions and this is where you will see which one describes you better.`
          : `Not comparable yet — ${need} more ${SYSTEM_LABEL[short]} rating${need === 1 ? '' : 's'} needed before the two can be told apart.`,
    };
  }

  const ia = wilsonInterval(creditOf(ta), na);
  const ib = wilsonInterval(creditOf(tb), nb);
  if (ia.lo > ib.hi) return { leader: a, message: leadCopy(a, b) };
  if (ib.lo > ia.hi) return { leader: b, message: leadCopy(b, a) };
  return {
    leader: null,
    message: `On ${na + nb} ratings the two are too close to separate. That is a real result, not a missing one — both systems are landing about equally for you.`,
  };
}

function leadCopy(winner: AstroSystem, loser: AstroSystem): string {
  return `${SYSTEM_LABEL[winner]} readings have been landing for you more often than ${SYSTEM_LABEL[loser]} ones. That says something about which language describes your life — not about which one is true.`;
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export function AccuracyDashboard({
  stats,
  daysTracked,
}: {
  stats: AccuracyStats;
  /** Distinct days the user showed up — shown beside the rating streak. */
  daysTracked: number;
}) {
  const overall = useMemo(() => overallTally(stats), [stats]);
  const verdict = useMemo(() => compareSystems(stats), [stats]);
  const ratedAreas = useMemo(
    () => LIFE_AREAS.filter((area) => stats.byArea[area].rated > 0),
    [stats],
  );
  const ratedPeriods = useMemo(
    () => HOROSCOPE_PERIODS.filter((p) => stats.byPeriod[p].rated > 0),
    [stats],
  );

  if (stats.totalRated === 0) {
    return <AccuracyEmptyState />;
  }

  return (
    <div className="col" style={{ gap: 0 }}>
      <div className="card">
        <RateBlock
          label="Overall"
          tally={overall}
          hint="Across every reading you have graded, in both traditions."
        />
        <div className="divider" />
        <div className="tiny muted" style={{ lineHeight: 1.55 }}>
          Read this for what it is: <strong>a record of your own judgement</strong>. You read the
          words before you graded them, so this measures how often a reading felt true to you — not
          whether astrology predicts anything. Keep it as a mirror, not a scoreboard.
        </div>
      </div>

      <Section title="Jyotish vs Western" term="sidereal">
        <div className="card">
          <div className="row" style={{ gap: 18, alignItems: 'flex-start' }}>
            {ASTRO_SYSTEMS.map((system) => (
              <RateBlock
                key={system}
                label={SYSTEM_LABEL[system]}
                tally={stats.bySystem[system]}
                mode={system}
              />
            ))}
          </div>
          <div
            className="banner banner--info"
            data-mode={verdict.leader ?? undefined}
            style={
              verdict.leader
                ? {
                    marginTop: 14,
                    background: 'var(--mode-soft)',
                    borderColor: 'var(--mode-line)',
                    color: 'var(--mode-ink)',
                  }
                : { marginTop: 14 }
            }
          >
            {verdict.message}
          </div>
        </div>
      </Section>

      {ratedAreas.length > 0 && (
        <Section title="By area of life">
          <div className="card">
            {ratedAreas.map((area) => (
              <BreakdownRow key={area} label={AREA_LABEL[area]} tally={stats.byArea[area]} />
            ))}
            <div className="tiny faint" style={{ marginTop: 8, lineHeight: 1.5 }}>
              Areas you have never rated are left out rather than shown as zero. A percentage only
              appears once an area has {MIN_FOR_NUMBER} decisive ratings behind it.
            </div>
          </div>
        </Section>
      )}

      {ratedPeriods.length > 0 && (
        <Section title="By length of reading">
          <div className="card">
            {ratedPeriods.map((period) => (
              <BreakdownRow key={period} label={PERIOD_LABEL[period]} tally={stats.byPeriod[period]} />
            ))}
            <div className="tiny faint" style={{ marginTop: 8, lineHeight: 1.5 }}>
              If your monthly readings land and your dailies do not, that is worth knowing — a
              longer window has more room to be right about something.
            </div>
          </div>
        </Section>
      )}

      <Section title="Consistency">
        <div className="card row" style={{ gap: 0 }}>
          <StatCell value={stats.streakDays} label={stats.streakDays === 1 ? 'day streak' : 'day streak'} />
          <StatCell value={daysTracked} label="days tracked" />
          <StatCell value={stats.totalRated} label="signals graded" />
        </div>
        <div className="tiny faint" style={{ marginTop: 8, lineHeight: 1.5 }}>
          Rating on the day still counts if you get to it by tomorrow — a streak that dies while you
          sleep is a bad streak.
        </div>
      </Section>
    </div>
  );
}

function StatCell({ value, label }: { value: number; label: string }) {
  return (
    <div style={{ flex: 1, textAlign: 'center' }}>
      <div className="h1" style={{ fontSize: '1.5rem', color: 'var(--accent-ink)' }}>
        {value}
      </div>
      <div className="tiny faint">{label}</div>
    </div>
  );
}

/** Sells the feature. Does not apologise for being empty. */
function AccuracyEmptyState() {
  return (
    <div className="card col" style={{ gap: 12 }}>
      <div className="h2">Find out which system actually reads you</div>
      <div className="small muted" style={{ lineHeight: 1.6 }}>
        Every reading Astrally casts is made of separate <strong>signals</strong> — one line each,
        one area of life each. Grade them: <em>landed</em>, <em>partly</em>, <em>missed</em>, or{' '}
        <em>couldn’t tell</em>. Takes a second.
      </div>
      <div className="col" style={{ gap: 10 }}>
        <Perk
          title="Jyotish against Western, on your own life"
          body="Two traditions, graded by the same person on the same events. Nobody can tell you which one fits you — your own record can."
        />
        <Perk
          title="The areas where it lands"
          body="You might find the work readings are uncanny and the relationship ones are noise. That pattern only shows up once there is a record."
        />
        <Perk
          title="Numbers that refuse to lie"
          body={`No percentage appears until an area has ${MIN_FOR_NUMBER} decisive ratings, and the two traditions are never declared different unless the maths can actually tell them apart.`}
        />
      </div>
      <div className="banner banner--gold" style={{ lineHeight: 1.5 }}>
        Straight up: this measures how often a reading <em>felt</em> true to you, because you read it
        before you graded it. It is a mirror on your own judgement, not proof that astrology works.
      </div>
    </div>
  );
}

function Perk({ title, body }: { title: string; body: string }) {
  return (
    <div className="row" style={{ alignItems: 'flex-start', gap: 10 }}>
      <span
        aria-hidden
        style={{ color: 'var(--gold-ink)', fontSize: '0.9rem', lineHeight: 1.5, flexShrink: 0 }}
      >
        ◆
      </span>
      <div style={{ minWidth: 0 }}>
        <div className="h3">{title}</div>
        <div className="tiny muted" style={{ lineHeight: 1.55, marginTop: 2 }}>
          {body}
        </div>
      </div>
    </div>
  );
}
